import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FileSpreadsheet,
  Download,
  Calendar,
  RefreshCw,
  Landmark,
  FileText,
  AlertCircle,
  Building2,
  Printer,
  Search,
} from "lucide-react";

export default function CustomerLedger() {
  const { user } = useAuth();
  const isDealer = user?.role === "dealer";
  const isStaff = ["admin", "manager", "accounts", "sales"].includes(user?.role);

  // Financial Year Presets: Default to FY 2025-26
  const [fromDate, setFromDate] = useState("2025-04-01");
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));

  // Staff dealer selector
  const { data: dealers = [] } = useQuery({
    queryKey: ["dealers-list-scoped"],
    queryFn: async () => {
      const { data } = await api.get("/dealers");
      return Array.isArray(data) ? data : [];
    },
    enabled: isStaff,
    staleTime: 1000 * 60 * 10,
  });

  const [selectedDealerCode, setSelectedDealerCode] = useState(
    user?.dealer_code || (dealers.length > 0 ? dealers[0].code : "TAMD0001")
  );

  useEffect(() => {
    if (isStaff && !selectedDealerCode && dealers.length > 0) {
      setSelectedDealerCode(dealers[0].code || "TAMD0001");
    }
  }, [isStaff, dealers, selectedDealerCode]);

  const targetCode = isDealer ? (user?.dealer_code || "TAMD0001") : selectedDealerCode;

  // Query Ind AS Ledger
  const { data: ledger, isLoading, refetch } = useQuery({
    queryKey: ["customer-ledger", targetCode, fromDate, toDate],
    queryFn: async () => {
      if (!targetCode) return null;
      const { data } = await api.get(`/ledger/${targetCode}?from_date=${fromDate}&to_date=${toDate}`);
      return data;
    },
    enabled: !!targetCode,
  });

  const printLedger = () => {
    window.print();
  };

  if (isLoading && !ledger) {
    return (
      <div className="space-y-6 p-1">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-36 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const entries = ledger?.entries || [];

  return (
    <div className="space-y-6" data-testid="customer-ledger-page">
      {/* Header & Date Range Filter */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Accounting & Audit</span>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px] font-semibold">
              Ind AS Compliant
            </Badge>
          </div>
          <h1 className="mt-1 text-3xl font-medium tracking-tight text-slate-900" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
            Customer Statement of Account
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Progressive 3-column running customer sub-ledger reflecting all SAP B1 Invoices, Bank Receipts, and Scheme Credit Notes.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Dealer Selector for Staff */}
          {isStaff && (
            <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs">
              <Building2 size={14} className="text-slate-500 ml-1.5" />
              <select
                value={selectedDealerCode || ""}
                onChange={(e) => setSelectedDealerCode(e.target.value)}
                className="bg-transparent text-slate-900 font-semibold focus:outline-none pr-2 cursor-pointer text-xs"
              >
                {dealers.map((d) => (
                  <option key={d.dealer_id} value={d.code}>
                    {d.name} ({d.code || "TAMD0000"})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date range */}
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs">
            <Calendar size={14} className="text-slate-400" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="font-medium text-slate-700 focus:outline-none bg-transparent"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="font-medium text-slate-700 focus:outline-none bg-transparent"
            />
          </div>

          <Button size="sm" variant="outline" onClick={printLedger} className="h-8 text-xs font-semibold">
            <Printer size={13} className="mr-1.5" /> Print Statement
          </Button>
        </div>
      </div>

      {/* Ind AS Statement Header Summary Card */}
      <Card className="p-6 border-slate-200 bg-white shadow-xs">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 border-b border-slate-100 pb-5 mb-5 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Entity / Distributor</div>
            <div className="text-sm font-semibold text-slate-900 mt-1">Bhawar Sales Corporation</div>
            <div className="text-slate-600 mt-0.5">GSTIN: 33AAEFB2116Q1ZK</div>
            <div className="text-slate-600">State: 33 - Tamil Nadu</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Customer Account</div>
            <div className="text-sm font-semibold text-slate-900 mt-1">{ledger?.dealer_info?.name || "Customer"}</div>
            <div className="text-slate-600 mt-0.5">BP Code: <span className="font-mono font-semibold">{ledger?.dealer_info?.code}</span></div>
            <div className="text-slate-600">GSTIN: {ledger?.dealer_info?.gstin}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Statement Period</div>
            <div className="text-sm font-semibold text-slate-900 mt-1">{new Date(fromDate).toLocaleDateString("en-IN", { dateStyle: "medium" })} to {new Date(toDate).toLocaleDateString("en-IN", { dateStyle: "medium" })}</div>
            <div className="text-slate-600 mt-0.5">Currency: <span className="font-semibold">INR (₹)</span></div>
            <div className="text-slate-600">Standard: Ind AS / Double-Entry</div>
          </div>
          <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200/80">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Closing Balance (Net Due)</div>
            <div className="text-2xl font-bold tracking-tight text-slate-900 mt-1 font-mono">
              {fmtINR(ledger?.closing_balance || 0)} <span className="text-sm font-semibold text-blue-600">{ledger?.closing_type}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Opening Balance: {fmtINR(ledger?.opening_balance || 0)} {ledger?.opening_type}</div>
          </div>
        </div>

        {/* 3-Column Running Balance Ledger Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[900px] border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100/70 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
                <th className="py-2.5 px-3 text-left">Posting Date</th>
                <th className="py-2.5 px-3 text-left">Voucher / Doc Type</th>
                <th className="py-2.5 px-3 text-left">Doc / Ref No.</th>
                <th className="py-2.5 px-3 text-left">Particulars & Narration</th>
                <th className="py-2.5 px-3 text-right">Debit (Dr ₹)</th>
                <th className="py-2.5 px-3 text-right">Credit (Cr ₹)</th>
                <th className="py-2.5 px-3 text-right">Running Balance (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {/* Opening Balance Row */}
              <tr className="bg-slate-50/70 font-semibold text-slate-800">
                <td className="py-2.5 px-3 font-mono">{new Date(fromDate).toLocaleDateString("en-IN")}</td>
                <td className="py-2.5 px-3" colSpan={3}>
                  OPENING BALANCE BROUGHT FORWARD (B/F)
                </td>
                <td className="py-2.5 px-3 text-right font-mono">—</td>
                <td className="py-2.5 px-3 text-right font-mono">—</td>
                <td className="py-2.5 px-3 text-right font-mono">
                  {fmtINR(ledger?.opening_balance || 0)} <span className="text-[10px] text-slate-600">{ledger?.opening_type}</span>
                </td>
              </tr>

              {entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500">
                    No transactions recorded during this period.
                  </td>
                </tr>
              ) : (
                entries.map((e, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-2.5 px-3 font-mono text-slate-600">{new Date(e.posting_date).toLocaleDateString("en-IN")}</td>
                    <td className="py-2.5 px-3 font-medium text-slate-900">{e.doc_type}</td>
                    <td className="py-2.5 px-3 font-mono text-blue-700">{e.doc_num || e.reference_no || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-600 max-w-xs truncate" title={e.narrative}>{e.narrative || "Tax Invoice / Payment receipt"}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-900">{e.debit > 0 ? fmtINR(e.debit) : "—"}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-emerald-700">{e.credit > 0 ? fmtINR(e.credit) : "—"}</td>
                    <td className="py-2.5 px-3 text-right font-mono font-semibold text-slate-900">
                      {fmtINR(e.running_balance)} <span className="text-[10px] text-slate-500 font-normal">{e.balance_type}</span>
                    </td>
                  </tr>
                ))
              )}

              {/* Period Totals Row */}
              <tr className="bg-slate-100/60 font-semibold border-t-2 border-slate-200 text-slate-900">
                <td className="py-3 px-3" colSpan={4}>TOTAL TRANSACTIONS IN PERIOD</td>
                <td className="py-3 px-3 text-right font-mono">{fmtINR(ledger?.period_debit_total || 0)}</td>
                <td className="py-3 px-3 text-right font-mono text-emerald-700">{fmtINR(ledger?.period_credit_total || 0)}</td>
                <td className="py-3 px-3 text-right font-mono">—</td>
              </tr>

              {/* Closing Balance Row */}
              <tr className="bg-slate-900 text-white font-semibold">
                <td className="py-3 px-3 font-mono">{new Date(toDate).toLocaleDateString("en-IN")}</td>
                <td className="py-3 px-3" colSpan={3}>
                  CLOSING BALANCE CARRIED DOWN (C/D)
                </td>
                <td className="py-3 px-3 text-right font-mono">—</td>
                <td className="py-3 px-3 text-right font-mono">—</td>
                <td className="py-3 px-3 text-right font-mono text-amber-300">
                  {fmtINR(ledger?.closing_balance || 0)} {ledger?.closing_type}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Ind AS Statutory Confirmation Footer */}
        <div className="mt-8 pt-4 border-t border-slate-200 text-[11px] text-slate-500 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-slate-700">Statutory Balance Confirmation Note:</p>
            <p className="mt-0.5">This statement represents the true and fair view of accounts as extracted from SAP Business One ERP under Ind AS framework.</p>
          </div>
          <div className="text-right whitespace-nowrap">
            <p className="font-semibold text-slate-800">For Bhawar Sales Corporation</p>
            <p className="mt-4 border-t border-slate-300 pt-1 text-[10px] text-slate-400">Authorized Signatory / Accounts Desk</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
